// Local IndexedDB using Dexie for complete offline usage
import Dexie from 'dexie'

// Database schema versioning
class LocalDb extends Dexie {
  constructor() {
    super('CafeMenuOfflineDb')

    this.version(1).stores({
      settings: '&key',
      sections: '++id, serverId, name, displayOrder, isActive, updatedAt, _dirty',
      tables: '++id, serverId, sectionId, name, capacity, isActive, updatedAt, _dirty',
      customers: '++id, serverId, fullName, phoneNumber, updatedAt, _dirty',
      orders: '++id, serverId, status, isPickup, isInternalOrder, tableId, customerId, totalAmount, createdAt, updatedAt, closedAt, _dirty',
      orderItems: '++id, serverId, orderId, menuItemId, quantity, unitPrice, totalPrice, updatedAt, _dirty',
      idMap: '&clientTempId, serverId, entity',
      syncJournal: '++id, type, startedAt, finishedAt, success'
    })

    this.version(2).stores({
      logs: '++id, level, scope, createdAt'
    })

    // v3: employees store for offline employee selection
    this.version(3).stores({
      employees: '++id, serverId, fullName, phoneNumber, isActive, updatedAt, _dirty'
    })

    // v4: menu items and categories for offline menu
    this.version(4).stores({
      menuItems: '++id, serverId, name, description, price, categoryId, image, photos, isPopular, isNew, isActive, updatedAt, _dirty',
      categories: '++id, serverId, name, description, icon, displayOrder, isActive, updatedAt, _dirty'
    })

    // v5: taxes store (active tax percentage for totals)
    this.version(5).stores({
      taxes: '++id, serverId, percentage, isActive, updatedAt, _dirty'
    })

    // Database is ready for use

    this.settings = this.table('settings')
    this.sections = this.table('sections')
    // Avoid shadowing Dexie's built-in db.tables getter
    this.restaurantTables = this.table('tables')
    this.customers = this.table('customers')
    this.orders = this.table('orders')
    this.orderItems = this.table('orderItems')
    this.idMap = this.table('idMap')
    this.syncJournal = this.table('syncJournal')
    this.logs = this.table('logs')
    this.employees = this.table('employees')
    this.menuItems = this.table('menuItems')
    this.categories = this.table('categories')
    this.taxes = this.table('taxes')
  }

  async getLastSyncAt() {
    const rec = await this.settings.get('lastSyncAt')
    return rec?.value || null
  }

  async setLastSyncAt(value) {
    await this.settings.put({ key: 'lastSyncAt', value })
  }
}

export const localDb = new LocalDb()

// Helper to mark records dirty for sync
export function markDirty(record) {
  return { ...record, _dirty: true, updatedAt: new Date().toISOString() }
}

// Temp ID generator for offline-created rows
export function createTempId(prefix) {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
}

// Convenience methods for orders
export const localOrders = {
  async createPickupOrder() {
    const now = new Date().toISOString()
    const order = {
      serverId: null,
      status: 0, // Open
      isPickup: true,
      isInternalOrder: false,
      tableId: null,
      customerId: null,
      totalAmount: 0,
      createdAt: now,
      updatedAt: now,
      closedAt: null,
      _dirty: 1
    }
    const id = await localDb.orders.add(order)
    return { id, ...order }
  },

  async createInternalOrder(employeeId, notes) {
    const now = new Date().toISOString()
    const order = {
      serverId: null,
      status: 3, // Unpaid by default for internal
      isPickup: false,
      isInternalOrder: true,
      tableId: null,
      customerId: null,
      totalAmount: 0,
      createdAt: now,
      updatedAt: now,
      closedAt: null,
      employeeId: employeeId,
      notes: notes || '',
      _dirty: 1
    }
    const id = await localDb.orders.add(order)
    return { id, ...order }
  },

  async listOpenPickupAndInternal() {
    const all = await localDb.orders
      .where('status')
      .anyOf(0, 3)
      .toArray()
    return all.filter(o => o.isPickup || o.isInternalOrder)
  },

  async createTableOrder(tableId, tableName) {
    const now = new Date().toISOString()
    const order = {
      serverId: null,
      status: 0, // Open
      isPickup: false,
      isInternalOrder: false,
      tableId: tableId,
      customerId: null,
      totalAmount: 0,
      notes: `سفارش میز ${tableName}`,
      createdAt: now,
      updatedAt: now,
      closedAt: null,
      _dirty: 1
    }
    const id = await localDb.orders.add(order)
    return { id, ...order }
  },

  async getByTableId(tableId) {
    const order = await localDb.orders
      .where('tableId')
      .equals(tableId)
      .and(order => order.status === 0 || order.status === 3) // Open or Unpaid
      .first()
    
    if (!order) return null
    
    const items = await localDb.orderItems.where('orderId').equals(order.id).toArray()
    order.orderItems = items.map(it => ({
      id: it.id,
      menuItemId: it.menuItemId,
      quantity: it.quantity,
      unitPrice: it.unitPrice,
      totalPrice: it.totalPrice,
      notes: it.notes || '',
      createdAt: it.createdAt,
      menuItemName: it.menuItemName || `محصول #${it.menuItemId}`,
      menuItemImage: it.menuItemImage || ''
    }))
    order.status = typeof order.status === 'number' ? order.status : 0
    order.isPickup = !!order.isPickup
    order.isInternalOrder = !!order.isInternalOrder
    order.notes = order.notes || ''
    order.deliveryFee = Number(order.deliveryFee || 0)
    order.discountPercentage = Number(order.discountPercentage || 0)
    order.taxAmount = Number(order.taxAmount || 0)
    order.taxPercentage = Number(order.taxPercentage || 0)
    order.subtotalAmount = Number(order.subtotalAmount || 0)
    order.totalAmount = Number(order.totalAmount || 0)
    return order
  },

  async getById(id) {
    const order = await localDb.orders.get(Number(id))
    if (!order) return null
    const items = await localDb.orderItems.where('orderId').equals(order.id).toArray()
    order.orderItems = items.map(it => ({
      id: it.id,
      menuItemId: it.menuItemId,
      quantity: it.quantity,
      unitPrice: it.unitPrice,
      totalPrice: it.totalPrice,
      notes: it.notes || '',
      createdAt: it.createdAt,
      menuItemName: it.menuItemName || `محصول #${it.menuItemId}`,
      menuItemImage: it.menuItemImage || ''
    }))
    order.status = typeof order.status === 'number' ? order.status : 0
    order.isPickup = !!order.isPickup
    order.isInternalOrder = !!order.isInternalOrder
    order.notes = order.notes || ''
    order.deliveryFee = Number(order.deliveryFee || 0)
    order.discountPercentage = Number(order.discountPercentage || 0)
    order.taxAmount = Number(order.taxAmount || 0)
    order.taxPercentage = Number(order.taxPercentage || 0)
    order.subtotalAmount = Number(order.subtotalAmount || 0)
    order.totalAmount = Number(order.totalAmount || 0)
    return order
  },

  async recalcTotals(orderId) {
    const items = await localDb.orderItems.where('orderId').equals(orderId).toArray()
    const subtotal = items.reduce((s, it) => s + (Number(it.totalPrice) || 0), 0)
    const order = await localDb.orders.get(orderId)
    const deliveryFee = Number(order?.deliveryFee || 0)
    
    // Load active tax percentage from local DB (fallback to 0)
    // Use toArray() and filter to avoid IDBKeyRange issues with invalid values
    let taxPct = 0
    try {
      const allTaxes = await localDb.taxes.toArray()
      const activeTax = allTaxes.find(t => {
        const isActiveVal = t.isActive
        return isActiveVal === 1 || isActiveVal === true || isActiveVal === '1'
      })
      taxPct = Number(activeTax?.percentage || 0)
    } catch (err) {
      console.warn('Error loading taxes, using 0%:', err)
      taxPct = 0
    }
    
    const taxAmount = Math.round(subtotal * (taxPct / 100) * 100) / 100
    await localDb.orders.update(orderId, {
      subtotalAmount: subtotal,
      taxPercentage: taxPct,
      taxAmount,
      totalAmount: subtotal + taxAmount + deliveryFee,
      updatedAt: new Date().toISOString(),
      _dirty: 1
    })
  },

  async addItem(orderId, menuItem) {
    try {
      // Normalize and validate orderId
      const normalizedOrderId = Number(orderId)
      if (!Number.isFinite(normalizedOrderId) || normalizedOrderId <= 0) {
        throw new Error(`Invalid orderId: ${orderId}`)
      }

      // Normalize and validate menuItemId
      const normalizedMenuItemId = Number(menuItem?.id ?? menuItem?.serverId)
      if (!Number.isFinite(normalizedMenuItemId) || normalizedMenuItemId <= 0) {
        console.error('Invalid menuItem:', menuItem)
        throw new Error(`Invalid menuItemId - menuItem.id: ${menuItem?.id}, menuItem.serverId: ${menuItem?.serverId}`)
      }

      // Validate price
      const price = Number(menuItem?.price)
      if (!Number.isFinite(price) || price < 0) {
        throw new Error(`Invalid price: ${menuItem?.price}`)
      }

      console.log('✓ Validated:', { normalizedOrderId, normalizedMenuItemId, price })

      // Try find existing item
      // Use single-key query + JS filter to avoid key range issues across browsers
      const existing = await localDb.orderItems
        .where('orderId')
        .equals(normalizedOrderId)
        .and(it => Number(it.menuItemId) === normalizedMenuItemId)
        .first()
      
      if (existing) {
        const quantity = (existing.quantity || 0) + 1
        await localDb.orderItems.update(existing.id, {
          quantity,
          totalPrice: price * quantity,
          updatedAt: new Date().toISOString(),
          _dirty: 1
        })
        console.log('✓ Updated existing item:', existing.id)
      } else {
        const newItem = {
          serverId: null,
          orderId: normalizedOrderId,
          menuItemId: normalizedMenuItemId,
          quantity: 1,
          unitPrice: price,
          totalPrice: price,
          menuItemName: menuItem.name || '',
          menuItemImage: menuItem.image || '',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          _dirty: 1
        }
        const itemId = await localDb.orderItems.add(newItem)
        console.log('✓ Added new item:', itemId)
      }
      
      await this.recalcTotals(normalizedOrderId)
      console.log('✓ Recalculated totals for order:', normalizedOrderId)
    } catch (error) {
      console.error('❌ Error in addItem:', error)
      console.error('  orderId:', orderId)
      console.error('  menuItem:', menuItem)
      throw error
    }
  },

  async updateItemQuantity(orderItemId, newQuantity) {
    const it = await localDb.orderItems.get(orderItemId)
    if (!it) return
    const quantity = Math.max(1, Number(newQuantity) || 1)
    await localDb.orderItems.update(orderItemId, {
      quantity,
      totalPrice: (it.unitPrice || 0) * quantity,
      updatedAt: new Date().toISOString(),
      _dirty: 1
    })
    await this.recalcTotals(it.orderId)
  },

  async removeItem(orderItemId) {
    const it = await localDb.orderItems.get(orderItemId)
    if (!it) return
    const orderId = it.orderId
    await localDb.orderItems.delete(orderItemId)
    await this.recalcTotals(orderId)
  },

  async closeOrder(orderId, paymentData) {
    try {
      const order = await localDb.orders.get(orderId)
      if (!order) {
        throw new Error('Order not found')
      }

      const now = new Date().toISOString()
      const updatedOrder = {
        ...order,
        status: 1, // Closed/Paid status (1 = Closed, 3 = Unpaid)
        closedAt: now,
        updatedAt: now,
        notes: paymentData.notes || order.notes,
        paymentMethod: paymentData.paymentMethod,
        cashAmount: paymentData.cashAmount || 0,
        posAmount: paymentData.posAmount || 0,
        cardTransferAmount: paymentData.cardTransferAmount || 0,
        _dirty: 1 // Mark for sync
      }

      await localDb.orders.update(orderId, updatedOrder)
      return updatedOrder
    } catch (error) {
      console.error('Error closing order:', error)
      throw error
    }
  },

  async updateOrder(orderId, updateData) {
    try {
      const order = await localDb.orders.get(orderId)
      if (!order) {
        throw new Error('Order not found')
      }

      const now = new Date().toISOString()
      const updatedOrder = {
        ...order,
        ...updateData,
        updatedAt: now,
        _dirty: 1 // Mark for sync
      }

      await localDb.orders.update(orderId, updatedOrder)
      return updatedOrder
    } catch (error) {
      console.error('Error updating order:', error)
      throw error
    }
  }
}

// Lightweight helpers for sections/tables used in offline UI
export const localLayout = {
  async listSectionsWithTables() {
    const sections = await localDb.sections.toArray()
    if (sections.length === 0) {
      // Return a default placeholder section so tabs render in offline-first UI
      return [
        {
          id: 1,
          serverId: null,
          name: 'سالن اصلی',
          description: 'بخش پیش‌فرض (آفلاین)',
          displayOrder: 1,
          isActive: true,
          tables: []
        }
      ]
    }
    const allTables = await localDb.restaurantTables.toArray()
    const sectionIdToTables = new Map()
    for (const t of allTables) {
      const arr = sectionIdToTables.get(t.sectionId) || []
      arr.push(t)
      sectionIdToTables.set(t.sectionId, arr)
    }
    return sections
      .sort((a,b) => (a.displayOrder||0) - (b.displayOrder||0))
      .map(s => ({ ...s, tables: sectionIdToTables.get(s.id) || [] }))
  }
}

// Employees offline cache helpers
export const localEmployees = {
  async upsertMany(employees) {
    const now = new Date().toISOString()
    const tx = localDb.transaction('rw', localDb.employees, async () => {
      for (const e of employees) {
        // Normalize isActive to 0 or 1 for IndexedDB compatibility
        const isActiveNormalized = (e.isActive !== false && e.isActive !== 0 && e.isActive !== '0') ? 1 : 0
        
        const rec = {
          serverId: e.id ?? e.serverId ?? null,
          fullName: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(),
          phoneNumber: e.phoneNumber || '',
          isActive: isActiveNormalized,
          updatedAt: e.updatedAt || now,
          _dirty: 0
        }
        if (e.id) {
          const existing = await localDb.employees.where({ serverId: e.id }).first()
          if (existing) {
            await localDb.employees.update(existing.id, rec)
          } else {
            await localDb.employees.add(rec)
          }
        } else {
          await localDb.employees.add(rec)
        }
      }
    })
    return tx
  },

  async listActive() {
    const all = await localDb.employees.toArray()
    return all
      .filter(e => e.isActive === 1 || e.isActive === true)
      .map(e => ({
        ...e,
        // Use local id as primary id, serverId as fallback
        id: e.id || e.serverId
      }))
  },

  async clearAll() {
    await localDb.employees.clear()
  }
}

// Sections offline cache helpers
export const localSections = {
  async upsertMany(sections) {
    const now = new Date().toISOString()
    const tx = localDb.transaction('rw', localDb.sections, async () => {
      for (const s of sections) {
        // Normalize isActive to 0 or 1 for IndexedDB compatibility
        const isActiveNormalized = (s.isActive !== false && s.isActive !== 0 && s.isActive !== '0') ? 1 : 0
        
        const rec = {
          serverId: s.id ?? s.serverId ?? null,
          name: s.name || '',
          description: s.description || '',
          displayOrder: s.displayOrder || 0,
          isActive: isActiveNormalized,
          updatedAt: s.updatedAt || now,
          _dirty: 0
        }
        if (s.id) {
          const existing = await localDb.sections.where({ serverId: s.id }).first()
          if (existing) {
            await localDb.sections.update(existing.id, rec)
          } else {
            await localDb.sections.add(rec)
          }
        } else {
          await localDb.sections.add(rec)
        }
      }
    })
    return tx
  },

  async listActive() {
    const all = await localDb.sections.toArray()
    return all
      .filter(s => s.isActive === 1 || s.isActive === true)
      .map(s => ({
        ...s,
        // Use local id as primary id, serverId as fallback
        id: s.id || s.serverId
      }))
  },

  async clearAll() {
    await localDb.sections.clear()
  }
}

// Tables offline cache helpers
export const localTables = {
  async upsertMany(tables) {
    const now = new Date().toISOString()
    const tx = localDb.transaction('rw', localDb.restaurantTables, async () => {
      for (const t of tables) {
        // Normalize isActive to 0 or 1 for IndexedDB compatibility
        const isActiveNormalized = (t.isActive !== false && t.isActive !== 0 && t.isActive !== '0') ? 1 : 0
        
        const rec = {
          serverId: t.id ?? t.serverId ?? null,
          sectionId: t.sectionId || null,
          name: t.name || '',
          description: t.description || '',
          capacity: t.capacity || 4,
          displayOrder: t.displayOrder || 0,
          isActive: isActiveNormalized,
          updatedAt: t.updatedAt || now,
          _dirty: 0
        }
        if (t.id) {
          const existing = await localDb.restaurantTables.where({ serverId: t.id }).first()
          if (existing) {
            await localDb.restaurantTables.update(existing.id, rec)
          } else {
            await localDb.restaurantTables.add(rec)
          }
        } else {
          await localDb.restaurantTables.add(rec)
        }
      }
    })
    return tx
  },

  async listActive() {
    const all = await localDb.restaurantTables.toArray()
    return all
      .filter(t => t.isActive === 1 || t.isActive === true)
      .map(t => ({
        ...t,
        // Use local id as primary id, serverId as fallback
        id: t.id || t.serverId
      }))
  },

  async clearAll() {
    await localDb.restaurantTables.clear()
  }
}

// Menu items offline cache helpers
export const localMenuItems = {
  async upsertMany(menuItems) {
    const now = new Date().toISOString()
    const tx = localDb.transaction('rw', localDb.menuItems, async () => {
      for (const item of menuItems) {
        // Normalize boolean values to 0 or 1 for IndexedDB compatibility
        const isActiveNormalized = (item.isActive !== false && item.isActive !== 0 && item.isActive !== '0') ? 1 : 0
        const isPopularNormalized = (item.isPopular === true || item.isPopular === 1 || item.isPopular === '1') ? 1 : 0
        const isNewNormalized = (item.isNew === true || item.isNew === 1 || item.isNew === '1') ? 1 : 0
        
        const rec = {
          serverId: item.id ?? item.serverId ?? null,
          name: item.name || '',
          description: item.description || '',
          price: item.price || 0,
          categoryId: item.categoryId || null,
          image: item.image || '',
          photos: item.photos || [],
          isPopular: isPopularNormalized,
          isNew: isNewNormalized,
          isActive: isActiveNormalized,
          updatedAt: item.updatedAt || now,
          _dirty: 0
        }
        if (item.id) {
          const existing = await localDb.menuItems.where({ serverId: item.id }).first()
          if (existing) {
            await localDb.menuItems.update(existing.id, rec)
          } else {
            await localDb.menuItems.add(rec)
          }
        } else {
          await localDb.menuItems.add(rec)
        }
      }
    })
    return tx
  },

  async listActive() {
    const all = await localDb.menuItems.toArray()
    // Map items to ensure they have a valid id field for compatibility
    return all
      .filter(item => item.isActive === 1 || item.isActive === true)
      .map(item => ({
        ...item,
        // Use local id as primary id, serverId as fallback
        id: item.id || item.serverId
      }))
  },

  async clearAll() {
    await localDb.menuItems.clear()
  }
}

// Categories offline cache helpers
export const localCategories = {
  async upsertMany(categories) {
    const now = new Date().toISOString()
    const tx = localDb.transaction('rw', localDb.categories, async () => {
      for (const c of categories) {
        // Normalize isActive to 0 or 1 for IndexedDB compatibility
        const isActiveNormalized = (c.isActive !== false && c.isActive !== 0 && c.isActive !== '0') ? 1 : 0
        
        const rec = {
          serverId: c.id ?? c.serverId ?? null,
          name: c.name || '',
          description: c.description || '',
          icon: c.icon || '',
          displayOrder: c.displayOrder || 0,
          isActive: isActiveNormalized,
          updatedAt: c.updatedAt || now,
          _dirty: 0
        }
        if (c.id) {
          const existing = await localDb.categories.where({ serverId: c.id }).first()
          if (existing) {
            await localDb.categories.update(existing.id, rec)
          } else {
            await localDb.categories.add(rec)
          }
        } else {
          await localDb.categories.add(rec)
        }
      }
    })
    return tx
  },

  async listActive() {
    const all = await localDb.categories.toArray()
    return all
      .filter(c => c.isActive === 1 || c.isActive === true)
      .map(c => ({
        ...c,
        // Use local id as primary id, serverId as fallback
        id: c.id || c.serverId,
        // Ensure serverId is always available for comparison with menu items
        serverId: c.serverId || c.id
      }))
  },

  async clearAll() {
    await localDb.categories.clear()
  }
}

// Taxes offline cache helpers
export const localTaxes = {
  async upsertMany(taxes) {
    const now = new Date().toISOString()
    const tx = localDb.transaction('rw', localDb.taxes, async () => {
      for (const t of taxes) {
        // Normalize isActive to a valid number (0 or 1) for IndexedDB
        const isActiveRaw = t.isActive ?? t.IsActive ?? false
        const isActiveNormalized = (isActiveRaw === true || isActiveRaw === 1 || isActiveRaw === '1') ? 1 : 0
        
        const rec = {
          serverId: t.id ?? t.serverId ?? null,
          percentage: Number(t.percentage ?? t.Percentage ?? 0),
          isActive: isActiveNormalized,
          updatedAt: t.updatedAt || now,
          _dirty: 0
        }
        if (t.id) {
          const existing = await localDb.taxes.where({ serverId: t.id }).first()
          if (existing) {
            await localDb.taxes.update(existing.id, rec)
          } else {
            await localDb.taxes.add(rec)
          }
        } else {
          await localDb.taxes.add(rec)
        }
      }
    })
    return tx
  },

  async getActive() {
    try {
      // Use toArray() to avoid IDBKeyRange issues
      const allTaxes = await localDb.taxes.toArray()
      const active = allTaxes.find(t => t.isActive === 1)
      return active || { percentage: 0, isActive: 0 }
    } catch (err) {
      console.warn('Error loading active tax:', err)
      return { percentage: 0, isActive: 0 }
    }
  },

  async clearAll() {
    await localDb.taxes.clear()
  }
}

// Data initialization service
export const dataInitializer = {
  async resetDatabase() {
    try {
      console.log('🗑️ Resetting local database...')
      await localDb.close()
      await localDb.delete()
      // Clear all data from existing tables
      await Promise.all([
        localDb.settings.clear(),
        localDb.sections.clear(),
        localDb.restaurantTables.clear(),
        localDb.customers.clear(),
        localDb.orders.clear(),
        localDb.orderItems.clear(),
        localDb.employees.clear(),
        localDb.menuItems.clear(),
        localDb.categories.clear()
      ])
      console.log('✅ Database reset successfully')
    } catch (error) {
      console.error('❌ Error resetting database:', error)
      throw error
    }
  },

  async initializeFromAPI() {
    try {
      console.log('🔄 Initializing local database from API...')
      
      // Test database connection first
      try {
        await localDb.open()
        console.log('✅ Database connection successful')
      } catch (dbError) {
        console.warn('⚠️ Database connection failed, attempting reset...', dbError)
        await this.resetDatabase()
      }
      
      // Import API services
      const { sectionsAPI, menuItemsAPI, categoriesAPI, employeesAPI, taxAPI } = await import('./api.js')
      
      // Fetch all data from API
      const [sectionsRes, menuItemsRes, categoriesRes, employeesRes, taxesRes] = await Promise.all([
        sectionsAPI.getAll(),
        menuItemsAPI.getAll(),
        categoriesAPI.getAll(),
        employeesAPI.getAll(),
        taxAPI.getAll()
      ])
      
      // Extract tables from sections data
      const allTables = []
      sectionsRes.data?.forEach(section => {
        if (section.tables && Array.isArray(section.tables)) {
          section.tables.forEach(table => {
            allTables.push({
              ...table,
              sectionId: section.id || section.serverId
            })
          })
        }
      })
      
      // Store in local database
      await Promise.all([
        localSections.upsertMany(sectionsRes.data || []),
        localTables.upsertMany(allTables),
        localMenuItems.upsertMany(menuItemsRes.data || []),
        localCategories.upsertMany(categoriesRes.data || []),
        localEmployees.upsertMany(employeesRes.data || []),
        localTaxes.upsertMany(taxesRes.data || [])
      ])
      
      console.log('✅ Local database initialized successfully')
      return {
        sections: sectionsRes.data?.length || 0,
        tables: allTables.length,
        menuItems: menuItemsRes.data?.length || 0,
        categories: categoriesRes.data?.length || 0,
        employees: employeesRes.data?.length || 0,
        taxes: (taxesRes.data || []).length
      }
    } catch (error) {
      console.error('❌ Error initializing local database:', error)
      // If it's a database error, try to reset and retry once
      if (error.name === 'OpenFailedError' || error.name === 'DexieError') {
        console.log('🔄 Attempting database reset and retry...')
        try {
          await this.resetDatabase()
          // Retry the initialization
          return await this.initializeFromAPI()
        } catch (retryError) {
          console.error('❌ Retry failed:', retryError)
          throw new Error('Database initialization failed. Please refresh the page and try again.')
        }
      }
      throw error
    }
  },

  async clearAllData() {
    try {
      console.log('🗑️ Clearing all local data...')
      await Promise.all([
        localSections.clearAll(),
        localTables.clearAll(),
        localMenuItems.clearAll(),
        localCategories.clearAll(),
        localEmployees.clearAll(),
        localOrders.clearAll()
      ])
      console.log('✅ All local data cleared')
    } catch (error) {
      console.error('❌ Error clearing local data:', error)
      throw error
    }
  },

  async getDataStatus() {
    try {
      const [sections, tables, menuItems, categories, employees, orders] = await Promise.all([
        localSections.listActive(),
        localTables.listActive(),
        localMenuItems.listActive(),
        localCategories.listActive(),
        localEmployees.listActive(),
        localOrders.listAll()
      ])
      
      return {
        sections: sections.length,
        tables: tables.length,
        menuItems: menuItems.length,
        categories: categories.length,
        employees: employees.length,
        orders: orders.length,
        lastUpdated: new Date().toISOString()
      }
    } catch (error) {
      console.error('❌ Error getting data status:', error)
      return null
    }
  }
}


